import React, { useState } from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { CheckCircle } from 'lucide-react';
export function GettingStartedPage() {
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const pingCode = {
    curl: `curl https://api.obseq.com/v1/ping \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    node: `const obseq = require('obseq');
obseq.apiKey = 'YOUR_API_KEY';

const response = await obseq.ping();
console.log(response);`,
    python: `import obseq
obseq.api_key = 'YOUR_API_KEY'

response = obseq.Ping.retrieve()
print(response)`,
    go: `package main

import "github.com/obseq/obseq-go"

func main() {
    client := obseq.New("YOUR_API_KEY")
    response, _ := client.Ping()
}`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'Guides',
    href: '/docs/guides'
  }, {
    label: 'Getting Started',
    href: '/docs/guides/getting-started'
  }]} onThisPage={[{
    title: 'Environments',
    id: 'environments'
  }, {
    title: 'Get API Keys',
    id: 'api-keys'
  }, {
    title: 'Make Your First Request',
    id: 'first-request'
  }, {
    title: 'Next Steps',
    id: 'next-steps'
  }]} data-id="element-1460">
      <h1 data-id="element-1461">Getting Started</h1>
      <p className="lead" data-id="element-1462">
        Learn how to set up your Obseq account and make your first API request
        in minutes.
      </p>

      <h2 id="environments" data-id="element-1463">Environments</h2>
      <p data-id="element-1464">Obseq provides two environments for development and production use:</p>

      <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1 w-fit my-4" data-id="element-1465">
        <button onClick={() => setEnvironment('sandbox')} className={`px-4 py-2 text-sm font-medium rounded transition-colors ${environment === 'sandbox' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1466">
          Sandbox
        </button>
        <button onClick={() => setEnvironment('production')} className={`px-4 py-2 text-sm font-medium rounded transition-colors ${environment === 'production' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-1467">
          Production
        </button>
      </div>

      <ul data-id="element-1468">
        <li data-id="element-1469">
          <strong data-id="element-1470">Sandbox:</strong> Test your integration without moving real
          money. All API calls are simulated.
        </li>
        <li data-id="element-1471">
          <strong data-id="element-1472">Production:</strong> Live environment for real transactions.
          Requires KYB verification.
        </li>
      </ul>

      <Callout type="tip" data-id="element-1473">
        Always develop and test in Sandbox before moving to Production.
      </Callout>

      <h2 id="api-keys" data-id="element-1474">Get API Keys</h2>
      <p data-id="element-1475">To authenticate with the Obseq API, you'll need an API key:</p>

      <ol data-id="element-1476">
        <li data-id="element-1477">Log in to your Obseq dashboard</li>
        <li data-id="element-1478">Navigate to Settings → API Keys</li>
        <li data-id="element-1479">Click "Create API Key"</li>
        <li data-id="element-1480">Select the environment (Sandbox or Production)</li>
        <li data-id="element-1481">Copy and securely store your API key</li>
      </ol>

      <Callout type="warning" data-id="element-1482">
        Never share your API keys or commit them to version control. Use
        environment variables instead.
      </Callout>

      <h2 id="first-request" data-id="element-1483">Make Your First Request</h2>
      <p data-id="element-1484">Test your API key with a simple ping request:</p>

      <CodeBlock code={pingCode} data-id="element-1485" />

      <p data-id="element-1486">A successful response will return:</p>

      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto" data-id="element-1487">
        <code data-id="element-1488">{`{
  "status": "ok",
  "timestamp": "2024-01-15T10:30:00Z"
}`}</code>
      </pre>

      <h2 id="next-steps" data-id="element-1489">Next Steps</h2>
      <p data-id="element-1490">Now that you've made your first request, explore these topics:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6" data-id="element-1491">
        {[{
        title: 'Authentication',
        href: '/docs/guides/authentication',
        desc: 'Learn about API authentication'
      }, {
        title: 'Webhooks',
        href: '/docs/guides/webhooks',
        desc: 'Receive real-time events'
      }, {
        title: 'Payments API',
        href: '/docs/api-reference/payments',
        desc: 'Send and receive money'
      }, {
        title: 'Wallets API',
        href: '/docs/api-reference/wallets',
        desc: 'Manage balances'
      }].map(item => <a key={item.href} href={`#${item.href}`} className="group p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all" data-id="element-1492">
            <h4 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors" data-id="element-1493">
              {item.title}
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1494">{item.desc}</p>
          </a>)}
      </div>
    </DocsLayout>;
}